import { CSSProperties, ReactNode } from 'react';
import MuiModal from '@mui/material/Modal';
import { styled } from '@mui/material/styles';
import CancelIcon from '@/assets/cancel2.svg?react';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import { motion } from 'motion/react';

const Modal = ({
  open,
  title,
  children,
  onClose,
  className,
  bodyStyle,
  actions,
  actionsError,
  actionsErrorKey,
  generalErrors,
  isActionBusy = false,
}: {
  open: boolean;
  title: ReactNode;
  children: ReactNode;
  onClose: () => void;
  className?: string;
  bodyStyle?: CSSProperties;
  actions?: ReactNode;
  actionsError?: ReactNode;
  actionsErrorKey?: number | null;
  generalErrors?: string[];
  isActionBusy?: boolean;
}) => {
  return (
    <MuiModal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-title"
    >
      <Root className={className} role="dialog" aria-modal="true">
        <Title>
          <Label id="modal-title">{title}</Label>
          <IconButton
            sx={{ padding: 0, width: 28, height: 28 }}
            onClick={onClose}
            aria-label="Close modal"
            disabled={isActionBusy}
          >
            {isActionBusy ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <CancelIcon />
            )}
          </IconButton>
        </Title>
        <Body style={bodyStyle}>
          {children}
          {generalErrors && generalErrors.length > 0 && (
            <GeneralErrorsContainer>
              {generalErrors.map((error, index) => (
                <GeneralErrorMessage key={index}>
                  {error}
                </GeneralErrorMessage>
              ))}
            </GeneralErrorsContainer>
          )}
        </Body>
        <Actions>
          {actionsError ? (
            <ActionsMessage
              key={actionsErrorKey}
              initial={{ transform: 'translateX(-10px)' }}
              animate={{ transform: 'translateX(0px)' }}
              transition={{
                type: 'spring',
                stiffness: 300,
                duration: 0.3,
              }}
              role="alert"
            >
              {actionsError}
            </ActionsMessage>
          ) : null}
          <ActionButtons>{actions}</ActionButtons>
        </Actions>
      </Root>
    </MuiModal>
  );
};

const Root = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  maxHeight: '95vh',
  width: 400,
  borderRadius: theme.radii.xs,
  display: 'flex',
  flexDirection: 'column',
  background: theme.palette.background.paper,
  padding: theme.spacing(3),
}));

const Title = styled('div')(({ theme }) => ({
  display: 'flex',
  paddingBottom: theme.spacing(2),

  svg: {
    width: '24px',
    height: '24px',

    path: {
      stroke: theme.palette.iconBlack,
    },
  },
}));

const Label = styled('div')({
  flexGrow: 1,
  fontSize: '24px',
  lineHeight: '28px',
  fontWeight: 600,
});

const Body = styled('div')({
  flexGrow: 1,
  overflowY: 'auto',
});

const Actions = styled('div')(({ theme }) => ({
  display: 'flex',
  paddingTop: theme.spacing(2),
}));

const ActionsMessage = styled(motion.div)(({ theme }) => ({
  flexGrow: 0,
  color: theme.palette.error.main,
}));

const ActionButtons = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(3),
  flexGrow: 1,
}));

const GeneralErrorsContainer = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

const GeneralErrorMessage = styled('div')(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20px',
  color: theme.palette.error.main,
}));

export default Modal;
